import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MapPin, Phone, Mail, CalendarClock, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BOOKING_STATUSES, PAYMENT_STATUSES, STATUS_STYLES, PAYMENT_STYLES, Pill, inr, fmtDate, fmtDateTime, fieldCls } from "@/components/admin/orderUtils";

const Section = ({ title, children, testId }) => (
  <section className="bg-white rounded-2xl border border-warm p-4 space-y-2" data-testid={testId}>
    <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold text-rose-500">{title}</h3>
    {children}
  </section>
);

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 text-sm"><span className="text-charcoal/50">{label}</span><span className="text-charcoal text-right font-medium" data-testid={testId}>{value ?? "—"}</span></div>
);

export const OrderDetailSheet = ({ orderId, onClose, onChanged }) => {
  const [o, setO] = useState(null);

  useEffect(() => {
    if (!orderId) return setO(null);
    api.get(`/admin/orders/${orderId}`).then(({ data }) => setO(data)).catch(() => toast.error("Could not load order"));
  }, [orderId]);

  const reload = async () => { const { data } = await api.get(`/admin/orders/${orderId}`); setO(data); onChanged(); };

  return (
    <Sheet open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-linen overflow-y-auto p-4 sm:p-6" data-testid="order-detail-sheet">
        <SheetHeader className="text-left">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Order detail</p>
          <SheetTitle className="font-display text-2xl text-charcoal" data-testid="order-detail-id">{orderId}</SheetTitle>
        </SheetHeader>
        {!o ? <div className="h-60 mt-6 rounded-2xl bg-white/60 animate-pulse" /> : <OrderBody o={o} reload={reload} />}
      </SheetContent>
    </Sheet>
  );
};

const OrderBody = ({ o, reload }) => {
  const f = o.financials;
  return (
    <div className="space-y-4 mt-5">
      <StatusControls o={o} reload={reload} />
      {o.confirmation_flow === "whatsapp" && <QuoteSection o={o} reload={reload} />}
      <Section title="Customer" testId="order-detail-customer">
        <p className="font-semibold text-charcoal" data-testid="order-detail-customer-name">{o.user_name}</p>
        <p className="text-sm text-charcoal/70 flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{o.user_email}</p>
        <p className="text-sm text-charcoal/70 flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{o.phone || "—"}</p>
        <Row label="Occasion" value={o.occasion || "Unspecified"} />
        <Row label="Ordered on" value={fmtDateTime(o.created_at)} />
        {o.budget && <Row label="Budget" value={`${o.budget.label || "Custom"}: ${inr(o.budget.min)} – ${inr(o.budget.max)}`} />}
        {o.notes && <Row label="Customer note" value={o.notes} />}
      </Section>
      <Section title="Items" testId="order-detail-items">
        {(o.items || []).map((it, i) => (
          <div key={i} className="flex justify-between gap-3 py-2 border-b border-warm/50 last:border-0" data-testid={`order-item-${i}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-charcoal">{it.name}</p>
              <p className="text-xs text-charcoal/50 capitalize">
                {it.kind || "product"} · {it.category}{[it.selected_weight, it.selected_flavor, it.selected_variant].filter(Boolean).map((v) => ` · ${v}`)}
                {it.price_type === "on_request" ? " · price on request" : ""}
              </p>
              {it.experience_details && <p className="text-xs text-charcoal/50">{[it.experience_details.date, it.experience_details.persons && `${it.experience_details.persons} persons`, it.experience_details.special_request].filter(Boolean).join(" · ")}</p>}
            </div>
            <div className="text-right text-sm shrink-0">
              <p className="font-semibold">{it.quantity} × {inr(it.price)}</p>
              <p className="text-xs text-charcoal/50">{it.cost_price != null ? `cost ${inr(it.cost_price)}` : "cost not set"}</p>
            </div>
          </div>
        ))}
        <div className="pt-2 space-y-1">
          {o.subtotal != null && <Row label="Subtotal" value={inr(o.subtotal)} />}
          <Row label="Delivery fee" value={inr(o.delivery_fee)} />
          {!!o.plan_ahead_discount && <Row label="Plan-ahead discount" value={`− ${inr(o.plan_ahead_discount)}`} />}
          <Row label="Total" value={inr(o.total)} testId="order-detail-total" />
          <Row label="Cost" value={inr(f.cost)} testId="order-detail-cost" />
          <Row label="Profit" value={inr(f.profit)} testId="order-detail-profit" />
          {f.items_missing_cost > 0 && <p className="text-xs text-amber-700 flex items-center gap-1 pt-1"><AlertTriangle className="w-3.5 h-3.5" />{f.items_missing_cost} item(s) have no cost price; profit counts them as zero cost.</p>}
        </div>
      </Section>
      <Section title="Delivery" testId="order-detail-delivery">
        <p className="text-sm text-charcoal flex gap-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5 text-charcoal/50" />{o.address || "—"}{o.pincode ? ` · ${o.pincode}` : ""}</p>
        <p className="text-sm text-charcoal flex gap-2"><CalendarClock className="w-4 h-4 shrink-0 mt-0.5 text-charcoal/50" />{fmtDate(o.selected_date || o.date)} · {o.time_slot || o.selected_time_slot || "—"}</p>
        <Row label="Delivery type" value={o.delivery_type || "—"} />
        <Row label="Assigned vendor" value={o.assigned_vendor || "—"} />
      </Section>
      <NotesSection o={o} reload={reload} />
    </div>
  );
};

const StatusControls = ({ o, reload }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };
  return (
    <Section title="Status" testId="order-detail-status">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-charcoal/50 space-y-1">
          <span className="flex items-center gap-2">Booking <Pill value={o.status} styles={STATUS_STYLES} testId="order-detail-booking-status" /></span>
          <select disabled={busy} value={o.status} className={`${fieldCls} w-full`} data-testid="order-status-select"
            onChange={(e) => run(() => api.patch(`/admin/bookings/${o.id}`, { status: e.target.value }), "Booking status updated")}>
            {BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-xs text-charcoal/50 space-y-1">
          <span className="flex items-center gap-2">Payment <Pill value={o.payment_status} styles={PAYMENT_STYLES} testId="order-detail-payment-status" /></span>
          <select disabled={busy} value={o.payment_status} className={`${fieldCls} w-full`} data-testid="order-payment-select"
            onChange={(e) => run(() => api.patch(`/admin/orders/${o.id}/payment`, { payment_status: e.target.value }), "Payment status updated")}>
            {PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
      </div>
      <Row label="Amount" value={inr(o.final_amount || o.total)} />
      {(o.payment_review || []).map((r, i) => (
        <p key={i} className="text-xs text-rose-600 flex items-start gap-1" data-testid={`order-payment-review-${i}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Unmatched payment {r.razorpay_payment_id} of {inr(r.amount)} ({r.status}) on {fmtDateTime(r.at)} — check in Razorpay and refund if captured.
        </p>
      ))}
    </Section>
  );
};

const APPROVAL_LABEL = { awaiting: "Awaiting quote", quoted: "Quoted · not yet approved", approved: "Customer approved" };

const QuoteSection = ({ o, reload }) => {
  const [amount, setAmount] = useState(o.final_amount ? String(o.final_amount) : "");
  const [approved, setApproved] = useState(!!o.customer_approved);
  const [note, setNote] = useState(o.quote_note || "");
  const [busy, setBusy] = useState(false);
  const locked = o.payment_status === "Paid" || o.status === "Cancelled";
  const save = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) return toast.error("Enter the final amount agreed with the customer");
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${o.id}/quote`, { final_amount: n, customer_approved: approved, note });
      toast.success(data.email_sent ? "Saved. Customer emailed to pay on CelebrAI." : "Quote saved");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save quote"); } finally { setBusy(false); }
  };
  return (
    <Section title="WhatsApp confirmation & final price" testId="order-detail-quote">
      <Row label="Approval" value={APPROVAL_LABEL[o.approval_status] || "—"} testId="order-detail-approval-status" />
      <Row label="Original estimate" value={inr(o.total)} />
      {o.quote_updated_at && <Row label="Last updated" value={`${fmtDateTime(o.quote_updated_at)} · ${o.quote_updated_by || "Admin"}`} />}
      <label className="text-xs text-charcoal/50 block pt-1">Final amount agreed (₹)
        <input type="number" min="1" value={amount} disabled={locked} onChange={(e) => setAmount(e.target.value)} className={`${fieldCls} w-full mt-1`} data-testid="order-quote-amount-input" />
      </label>
      <label className="flex items-center gap-2 text-sm text-charcoal">
        <input type="checkbox" checked={approved} disabled={locked} onChange={(e) => setApproved(e.target.checked)} className="accent-rose-600 w-4 h-4" data-testid="order-quote-approved-checkbox" />
        Customer approved this amount on WhatsApp
      </label>
      <textarea value={note} disabled={locked} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What was agreed (availability, custom requirements)…" className={`${fieldCls} w-full`} data-testid="order-quote-note-input" />
      {locked ? (
        <p className="text-xs text-charcoal/50" data-testid="order-quote-locked">{o.payment_status === "Paid" ? "Paid — the amount can no longer change." : "Order cancelled."}</p>
      ) : (
        <button onClick={save} disabled={busy} className="pill-btn pill-btn-primary w-full !py-2 text-sm" data-testid="order-quote-save-btn">
          {busy ? "Saving..." : approved ? "Save & let customer pay on CelebrAI" : "Save quote"}
        </button>
      )}
      <p className="text-[11px] text-charcoal/50">The customer pays this exact amount on their CelebrAI bookings page. No payment links are sent on WhatsApp.</p>
    </Section>
  );
};

const NotesSection = ({ o, reload }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/orders/${o.id}/notes`, { text }); setText(""); await reload(); } catch { toast.error("Could not save note"); } finally { setBusy(false); }
  };
  return (
    <Section title="Internal notes" testId="order-detail-notes">
      {o.admin_notes.length === 0 && <p className="text-sm text-charcoal/50">No notes yet. Only admins can see these.</p>}
      {o.admin_notes.slice().reverse().map((n, i) => (
        <div key={i} className="rounded-xl bg-linen p-3" data-testid={`order-note-${i}`}>
          <p className="text-sm text-charcoal whitespace-pre-wrap">{n.text}</p>
          <p className="text-[11px] text-charcoal/50 mt-1 flex items-center gap-1"><StickyNote className="w-3 h-3" />{n.author} · {fmtDateTime(n.created_at)}</p>
        </div>
      ))}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note for the team..." className={`${fieldCls} w-full`} data-testid="order-note-input" />
      <button onClick={add} disabled={busy || !text.trim()} className="pill-btn pill-btn-primary w-full !py-2 text-sm" data-testid="order-note-save-btn">{busy ? "Saving..." : "Add note"}</button>
    </Section>
  );
};
